'use client';
import React from 'react';
import { Section, Container } from '@layout';
import { Card, ComboBox, Heading } from '@ui';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

/** Short, product-neutral lists for the public design-system page (not self-referential demo labels). */
const WORKSPACE_PICK_EN = ['Projects', 'Teams', 'Reports', 'Documents', 'Billing', 'Settings'];

const WORKSPACE_PICK_ES = [
  'Proyectos',
  'Equipos',
  'Informes',
  'Documentos',
  'Facturación',
  'Ajustes',
];

const SKILLS_EN = [
  'Front-end development',
  'Accessibility',
  'Design systems',
  'TypeScript',
  'UX research',
  'Performance',
];

const SKILLS_ES = [
  'Desarrollo front-end',
  'Accesibilidad',
  'Sistemas de diseno',
  'TypeScript',
  'Investigacion UX',
  'Rendimiento',
];

type ComboBoxDemoProps = {
  sectionID?: string;
  className?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
};

const ComboBoxDemo: React.FC<ComboBoxDemoProps> = ({
  isSpanish = false,
  sectionID = 'combobox',
  className,
  containerId,
  containerClasses,
}) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const singleItems = isSpanish ? WORKSPACE_PICK_ES : WORKSPACE_PICK_EN;
  const multiItems = isSpanish ? SKILLS_ES : SKILLS_EN;

  return (
    <Section sectionID={sectionID} className={className}>
      <Container containerId={containerId} containerClasses={containerClasses ?? (className || '')}>
        <Heading as='h3'>{t('Combobox / Autocomplete', 'ComboBox / Autocompletar')}</Heading>
        <p className='sub'>
          {t(
            'Single-select and multi-select with chips. Arrow keys + Enter, click to choose.',
            'Selección simple y múltiple con chips. Flechas + Enter, o haz clic para elegir.'
          )}
        </p>

        <div className='grid-2'>
          <Card>
            <div className='card-header'>
              <Heading as='h4'>{t('Single-select', 'Selección simple')}</Heading>
            </div>
            <ComboBox
              items={singleItems}
              idPrefix={`${sectionID}-single`}
              placeholder={t('Search workspaces…', 'Buscar espacios de trabajo…')}
            />
          </Card>

          <Card>
            <div className='card-header'>
              <Heading as='h4'>{t('Multi-select', 'Selección múltiple')}</Heading>
            </div>
            <ComboBox
              items={multiItems}
              idPrefix={`${sectionID}-multi`}
              placeholder={t('Add skills…', 'Agregar habilidades…')}
              multi
            />
          </Card>
        </div>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="combo" data-combo>
  <label class="hint" for="workspace">Workspace</label>
  <input id="workspace" class="combo-field" type="text" role="combobox" aria-expanded="false" />
</div>`}
        />
      </Container>
    </Section>
  );
};

export default ComboBoxDemo;
